import { SESSION_DATA_IDENTIFIER } from '../../../../api/utils/apollo/session/sessionToken.utils'
import localforage from '../../../../legacy/common/localforage'
import { removeRefreshToken } from '../../../../api/utils/auth/authStorage'
import { storeCurrentRedirectUrl } from '../../../../routes/logged-in/utils/loggedIn.utils'
import { getKeepLoggedIn, isSsoUser, getEmailFromJWT } from '../../../../utils/auth/keepLoggedIn'
import { isOnLoginPage } from '../../../../utils/auth/isLoginPage'
import { redirectToAuthService } from '../../../../utils/auth/redirectToAuthService'

/**
 * Handles SSO re-authentication when a user's session expires.
 *
 * This function checks if the user should be redirected to SSO re-authentication
 * instead of being logged out completely. It will redirect if:
 * - The user has "keep me logged in" enabled
 * - The user is an SSO user (not a password user with refresh tokens)
 * - We're not already on the login page (to avoid loops)
 *
 * @returns true if SSO re-auth was triggered, false to continue with normal logout
 */
export const handleSsoReauth = async (): Promise<boolean> => {
  // Don't trigger re-auth during initial login flow (on login or callback pages)
  if (isOnLoginPage()) {
    console.log('[SSO Re-auth] Skipping re-auth during login flow')
    return false
  }

  const keepLoggedIn = getKeepLoggedIn()
  if (!keepLoggedIn) {
    return false
  }

  // Check if this is an SSO user
  const sessionData = await localforage.getItem<{ token?: string }>(SESSION_DATA_IDENTIFIER)
  const token = sessionData?.token

  // Only SSO users need this flow - password users have refresh tokens that handle automatic renewal
  if (!token || !isSsoUser(token)) {
    return false
  }

  const email = getEmailFromJWT(token)
  if (!email) {
    console.error('[SSO Re-auth] No email found in JWT, falling back to normal logout')
    return false
  }

  console.log('[SSO Re-auth] Redirecting to SSO re-authentication')

  // Store current URL to return after auth
  storeCurrentRedirectUrl()

  // Clear the session and refresh token so the login flow starts fresh
  await Promise.all([localforage.removeItem(SESSION_DATA_IDENTIFIER), removeRefreshToken()])

  // Redirect to OAuth authorize with email - this will trigger SSO flow automatically
  await redirectToAuthService(email)
  return true
}
